/* ==========================================================================
   LUXERION v2 — motion + interactive widgets  (vanilla JS, no dependencies)
   Modules: scroll reveal · hero intro · scroll parallax · before/after slider
            · material configurator · expanding showroom panels · count-up.
   Honors prefers-reduced-motion. Re-inits inside the Shopify theme editor.
   ========================================================================== */
(function () {
  'use strict';
  document.documentElement.classList.add('lux-js');
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1. Scroll reveal ------------------------------------------------ */
  function initReveal(scope) {
    var nodes = (scope || document).querySelectorAll('.lux-reveal:not(.is-in), .lux-stagger:not(.is-in)');
    if (!nodes.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); obs.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---- 2. Hero intro (Ken Burns + word rise) --------------------------- */
  function initHero(scope) {
    (scope || document).querySelectorAll('.lux-hero:not(.is-ready), .lux-bio:not(.is-ready)').forEach(function (h) {
      var title = h.querySelector('[data-lux-words]');
      if (title && !title.dataset.luxDone) {
        var words = title.textContent.trim().split(/\s+/);
        title.innerHTML = words.map(function (w) { return '<span class="lux-word"><span>' + w + '</span></span>'; }).join(' ');
        title.dataset.luxDone = '1';
      }
      requestAnimationFrame(function () { h.classList.add('is-ready'); h.classList.add('is-in'); });
    });
  }

  /* ---- 3. Scroll parallax ([data-lux-parallax]) ------------------------ */
  var parallaxEls = [];
  function collectParallax(scope) {
    if (reduce) return;
    (scope || document).querySelectorAll('[data-lux-parallax]').forEach(function (el) {
      if (parallaxEls.indexOf(el) === -1) parallaxEls.push(el);
    });
  }
  var ticking = false;
  function onScroll() {
    if (ticking || !parallaxEls.length) return;
    ticking = true;
    requestAnimationFrame(function () {
      var vh = window.innerHeight;
      parallaxEls.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        var speed = parseFloat(el.getAttribute('data-lux-parallax')) || 0.15;
        var offset = (r.top + r.height / 2 - vh / 2) * speed * -1;
        offset = Math.max(-70, Math.min(70, offset));
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      ticking = false;
    });
  }

  /* ---- 4. Before / after slider --------------------------------------- */
  function initBeforeAfter(scope) {
    (scope || document).querySelectorAll('.lux-ba:not([data-lux-init])').forEach(function (ba) {
      ba.dataset.luxInit = '1';
      var range = ba.querySelector('.lux-ba__range');
      if (!range) return;
      function set(v) { ba.style.setProperty('--lux-pos', v + '%'); }
      set(range.value || 50);
      range.addEventListener('input', function () { set(range.value); });
    });
  }

  /* ---- 5. Material configurator --------------------------------------- */
  function initExplorer(scope) {
    (scope || document).querySelectorAll('.lux-mx:not([data-lux-init])').forEach(function (mx) {
      mx.dataset.luxInit = '1';
      var items = mx.querySelectorAll('.lux-mx__item');
      var panels = mx.querySelectorAll('.lux-mx__panel');
      function activate(id) {
        items.forEach(function (c) { c.setAttribute('aria-selected', c.dataset.target === id ? 'true' : 'false'); });
        panels.forEach(function (p) { if (p.dataset.panel === id) p.setAttribute('data-active', ''); else p.removeAttribute('data-active'); });
      }
      items.forEach(function (c) {
        c.addEventListener('mouseenter', function () { activate(c.dataset.target); });
        c.addEventListener('click', function () { activate(c.dataset.target); });
        c.addEventListener('focus', function () { activate(c.dataset.target); });
      });
      if (items[0]) activate(items[0].dataset.target);
    });
  }

  /* ---- 6. Expanding showroom panels ----------------------------------- */
  function initShowroom(scope) {
    (scope || document).querySelectorAll('.lux-showroom:not([data-lux-init])').forEach(function (sr) {
      sr.dataset.luxInit = '1';
      var panels = sr.querySelectorAll('.lux-panel');
      function open(p) { panels.forEach(function (x) { x.classList.toggle('is-open', x === p); }); }
      panels.forEach(function (p, i) {
        p.addEventListener('mouseenter', function () { open(p); });
        p.addEventListener('focus', function () { open(p); });
        if (i === 0) p.classList.add('is-open');
      });
    });
  }

  /* ---- 7. Count-up numbers ([data-lux-count]) ------------------------- */
  function runCount(el) {
    var target = parseFloat(el.getAttribute('data-lux-count')) || 0;
    var suffix = el.getAttribute('data-lux-suffix') || '';
    var prefix = el.getAttribute('data-lux-prefix') || '';
    if (reduce) { el.textContent = prefix + target.toLocaleString('nl-NL') + suffix; return; }
    var start = null, dur = 1600;
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = prefix + Math.round(target * eased).toLocaleString('nl-NL') + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  function initCount(scope) {
    var nodes = (scope || document).querySelectorAll('[data-lux-count]:not([data-lux-done])');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) { nodes.forEach(function (n) { n.dataset.luxDone = '1'; runCount(n); }); return; }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) { if (e.isIntersecting) { e.target.dataset.luxDone = '1'; runCount(e.target); obs.unobserve(e.target); } });
    }, { threshold: 0.5 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---- 8. Reduced-motion: pause background videos --------------------- */
  function initVideo(scope) {
    if (!reduce) return;
    (scope || document).querySelectorAll('.lux-hero__video, .lux-bg-video').forEach(function (v) {
      v.removeAttribute('autoplay');
      if (typeof v.pause === 'function') { try { v.pause(); v.currentTime = 0; } catch (e) {} }
    });
  }

  /* ---- reveal failsafe: force-show anything in or above the viewport --- */
  function sweepReveal() {
    var vh = window.innerHeight;
    document.querySelectorAll('.lux-reveal:not(.is-in), .lux-stagger:not(.is-in)').forEach(function (n) {
      if (n.getBoundingClientRect().top < vh * 0.96) n.classList.add('is-in');
    });
  }

  /* ---- init / re-init -------------------------------------------------- */
  function initAll(scope) {
    initReveal(scope); initHero(scope); collectParallax(scope);
    initBeforeAfter(scope); initExplorer(scope); initShowroom(scope); initCount(scope); initVideo(scope);
  }
  function boot() {
    initAll(document);
    window.addEventListener('scroll', sweepReveal, { passive: true });
    setTimeout(sweepReveal, 900);
    setTimeout(sweepReveal, 3000);
    if (!reduce) window.addEventListener('scroll', onScroll, { passive: true });
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/* ==========================================================================
   LUXERION CRO — sticky mobile add-to-cart  (added 2026-09-14)
   Self-contained: appended as its own IIFE so the motion modules above are
   untouched. The bar never owns cart logic — it forwards clicks to the real
   product form button, so variants, quantity, the cart drawer and the Shopify
   `add_to_cart` pixel event all behave exactly as they did before.
   ========================================================================== */
(function () {
  'use strict';

  function initStickyAtc(scope) {
    var bars = (scope || document).querySelectorAll('[data-lux-sticky-atc]');
    Array.prototype.forEach.call(bars, function (bar) {
      if (bar.__luxReady) return;
      bar.__luxReady = true;

      var form = document.querySelector(bar.getAttribute('data-lux-satc-form') || '');
      var realBtn = form ? form.querySelector('[name="add"], .product-form__submit') : null;
      var proxy = bar.querySelector('[data-lux-satc-button]');
      var priceOut = bar.querySelector('[data-lux-satc-price]');
      var priceSrc = document.querySelector(bar.getAttribute('data-lux-satc-price-src') || '');

      // Without a real button there is nothing safe to forward to — stay hidden.
      if (!realBtn || !proxy) return;

      // Keep the button clear of a floating chat launcher (see .lux-satc--chat).
      if (document.querySelector('shopify-chat, #ShopifyChat')) bar.classList.add('lux-satc--chat');

      proxy.addEventListener('click', function () {
        if (realBtn.disabled) return;
        realBtn.click();
      });

      // Mirror availability (variant switches toggle the real button's state).
      function syncState() {
        proxy.disabled = !!realBtn.disabled;
        var label = realBtn.querySelector('span');
        if (label && label.textContent.trim()) proxy.textContent = label.textContent.trim();
      }
      // Dawn's price element also carries visually-hidden labels ("Normale prijs",
      // "Aanbiedingsprijs") plus the inactive sale / sold-out variants. Copying its
      // whole textContent would print all of that, so pick the price actually shown.
      function syncPrice() {
        if (!priceOut || !priceSrc) return;
        var onSale = priceSrc.classList.contains('price--on-sale') || !!priceSrc.querySelector('.price--on-sale');
        var pick = priceSrc.querySelector(onSale ? '.price-item--sale' : '.price-item--regular');
        var text = pick ? pick.textContent : '';
        text = text.trim();
        if (text) priceOut.textContent = text;
      }
      if (window.MutationObserver) {
        new MutationObserver(syncState).observe(realBtn, { attributes: true, childList: true, subtree: true });
        if (priceSrc) new MutationObserver(syncPrice).observe(priceSrc, { childList: true, subtree: true, characterData: true });
      }
      syncState();
      syncPrice();

      // Only once the customer has actually started reading, and only while the real
      // button is off screen. Appearing instantly on load would cover the first product
      // image before anyone has seen the product.
      var MIN_SCROLL = 300;
      function shouldShow() {
        if ((window.pageYOffset || document.documentElement.scrollTop || 0) < MIN_SCROLL) return false;
        var r = realBtn.getBoundingClientRect();
        var below = r.top > (window.innerHeight || 0);   // CTA still further down the page
        var above = r.bottom < 0;                        // CTA already scrolled past
        return below || above;
      }
      function update() {
        bar.classList.toggle('is-visible', shouldShow());
        bar.setAttribute('aria-hidden', shouldShow() ? 'false' : 'true');
      }

      // Scroll/resize drive it directly. Deliberately no IntersectionObserver and
      // no requestAnimationFrame gate: rAF is suspended in a backgrounded tab, so a
      // boolean "ticking" latch could stay stuck and freeze the bar in the wrong
      // state. update() is one getBoundingClientRect plus a classList toggle, which
      // is cheap enough to run straight from a passive scroll listener.
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      document.addEventListener('visibilitychange', update);
      update();
    });
  }

  function boot() {
    initStickyAtc(document);
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (e) { initStickyAtc(e.target); });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
